import { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SEOHead } from '../components/SEOHead';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { FAQ } from '../components/FAQ';
import { BookingModal } from '../components/BookingModal';
import { 
  Shield, Droplets, Sparkles, Car, Sun, 
  CheckCircle, Phone, MapPin, Clock, Star,
  ArrowRight, ChevronRight, ChevronDown, MessageSquare
} from 'lucide-react';
import { ContactDropdown } from '../components/ContactDropdown';

export const HomePage = () => {
  const location = useLocation();
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(1);
  const [isServiceMenuOpen, setIsServiceMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsServiceMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openBooking = (packageLevel) => {
    setSelectedPackage(packageLevel);
    setIsBookingOpen(true);
  };

  const serviceOptions = [
    { name: 'Ceramic Coating', path: '/ceramic-coating-fresno', icon: <Shield className="w-5 h-5" /> },
    { name: 'Paint Correction', path: '/paint-correction-fresno', icon: <Sparkles className="w-5 h-5" /> },
    { name: 'Mobile Detailing', path: '/mobile-detailing-fresno', icon: <Car className="w-5 h-5" /> },
    { name: 'View Detailing Packages', path: '/#packages', icon: <CheckCircle className="w-5 h-5" /> },
  ];

  const services = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Ceramic Coating',
      description: 'Professional-grade ceramic protection for lasting shine and durability. Shield your paint from UV rays, contaminants, and minor scratches.',
      link: '/ceramic-coating-fresno'
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: 'Paint Correction',
      description: 'Multi-stage polishing to remove swirls, scratches, and oxidation. Restore your paint\'s true depth and mirror-like finish.',
      link: '/paint-correction-fresno'
    },
    {
      icon: <Car className="w-8 h-8" />,
      title: 'Mobile Detailing',
      description: 'Complete interior and exterior detailing at your location. Professional equipment and expertise delivered to your driveway.',
      link: '/mobile-detailing-fresno'
    },
    {
      icon: <Droplets className="w-8 h-8" />,
      title: 'Interior Detailing',
      description: 'Deep cleaning, leather conditioning, carpet extraction, and odor elimination. Transform your vehicle\'s interior.',
      link: '/#services'
    },
    {
      icon: <Sun className="w-8 h-8" />,
      title: 'Headlight Restoration',
      description: 'Restore clarity and brightness to oxidized headlights. Improve visibility and enhance your vehicle\'s appearance.',
      link: '/#services'
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Premium Protection',
      description: 'Advanced protection packages combining correction, coating, and maintenance for ultimate automotive care.',
      link: '/#packages'
    }
  ];

  const packages = [
    {
      name: 'Level I: The Maintenance Refresh',
      subtitle: 'Interior Wipe Down, Vacuum, Blow out + Full Exterior',
      price: '$99',
      priceLabel: '/starting',
      description: 'Maintain your vehicle\'s shine with our all-in-one Maintenance Package. This comprehensive service covers a meticulous exterior hand wash and foam bath, wheel detailing, interior vacuuming, and a protective spray sealant finish.',
      featured: false,
      packageLevel: 1
    },
    {
      name: 'Level II: Radiant (Full Detail)',
      subtitle: 'Full Interior + Exterior Detail',
      price: '$150',
      priceLabel: '/starting',
      description: 'Elevate your vehicle\'s appearance with our Signature Detail, designed to remove surface grime and restore interior freshness. This comprehensive service includes everything in the Maintenance Package, plus a deep interior scrub of all surfaces to ensure a spotless finish.',
      featured: true,
      packageLevel: 2
    },
    {
      name: 'Level III: The Elite Lustre',
      subtitle: 'Deep Full Interior + Exterior Detail',
      price: '$230',
      priceLabel: '/starting',
      description: 'This service includes everything in the Radiant package, plus a mechanical clay bar treatment to remove industrial fallout for perfectly smooth paint. We apply a 3-to-6 month Ceramic Coating for hydrophobic water beading and UV protection.',
      featured: false,
      packageLevel: 3
    }
  ];

  const galleryItems = [
    {
      label: 'Pet Hair Removal & Steam Treatment',
      before: '/gallery/home/before.jpg',
      after: '/gallery/home/after.jpg'
    },
    {
      label: 'Exterior Detail & Sealant',
      before: '/gallery/home/before1.jpg',
      after: '/gallery/home/after1.jpg'
    },
    {
      label: 'Paint Correction & Ceramic Coating',
      before: '/gallery/home/before2.jpg',
      after: '/gallery/home/after2.jpg'
    }
  ];

  const faqItems = [
    {
      question: 'How long does ceramic coating last?',
      answer: 'Our professional ceramic coatings can last anywhere from 2-5+ years depending on the package you choose and how well the vehicle is maintained. We offer various tiers of ceramic protection, from entry-level coatings that last 1-2 years to our premium coatings that provide 5+ years of protection.'
    },
    {
      question: 'Do you come to my location in Fresno?',
      answer: 'Yes! We are a fully mobile detailing service serving Fresno, Madera, Clovis, Chowchilla, and surrounding Central California areas. We bring all professional equipment to your home, office, or any convenient location.'
    },
    {
      question: 'What areas do you serve?',
      answer: 'We proudly serve the entire Central California region including Fresno, Madera, Clovis, Chowchilla, Sanger, Selma, Visalia, and surrounding communities. Contact us if you\'re outside these areas - we may still be able to accommodate you!'
    },
    {
      question: 'How long does a full detail take?',
      answer: 'Service times vary based on vehicle size and condition. A Maintenance Refresh typically takes 1-2 hours, a Full Detail takes 3-4 hours, and our Elite packages with paint correction and ceramic coating can take 6-8+ hours for optimal results.'
    },
    {
      question: 'Is ceramic coating worth it?',
      answer: 'Absolutely! Ceramic coating provides superior protection compared to traditional wax, lasting years instead of weeks. It protects against UV damage, chemical stains, bird droppings, and makes washing your car significantly easier with its hydrophobic properties.'
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <SEOHead
        title="Noble Detailing | Premium Auto Detailing & Ceramic Coating Fresno, Madera, Clovis"
        description="Fresno's premier mobile auto detailing studio. Specializing in Ceramic Coating, Paint Correction, and Luxury Mobile Detailing. Serving Fresno, Clovis, Madera & Chowchilla. Call 559-880-1818."
        keywords="auto detailing fresno, ceramic coating fresno, paint correction fresno, mobile detailing fresno, car detailing madera, ceramic coating clovis, auto detailing chowchilla, mobile car wash fresno"
      />

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingOpen} 
        onClose={() => setIsBookingOpen(false)} 
        packageLevel={selectedPackage}
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1762195340046-415140d8b1b2?w=1920&q=80"
            alt="Luxury car detailing in Fresno"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="font-accent text-cyan-400 text-lg font-semibold">01</span>
                <span className="text-gray-400 uppercase tracking-widest text-sm">Premium Mobile Service</span>
              </div>
              
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                <span className="gradient-text">NOBLE</span>
                <br />
                <span className="text-white/60 font-light">DETAILING</span>
              </h1>
              
              <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-transparent mb-8" />
              
              <p className="text-lg text-gray-300 mb-10 max-w-lg leading-relaxed">
                Professional automotive care delivered to your location. Specializing in <strong>ceramic coatings</strong>, <strong>paint correction</strong>, and complete detailing services in <strong>Fresno, Madera, Clovis & Surrounding Areas</strong>.
              </p>

              <div className="flex flex-wrap gap-8 mb-10">
                <div className="flex items-center gap-3">
                  <div className="w-0.5 h-12 bg-gradient-to-b from-cyan-500 to-transparent" />
                  <div>
                    <div className="font-accent text-3xl font-bold text-cyan-400">180+</div>
                    <div className="text-gray-500 text-sm uppercase tracking-wider">Satisfied Clients</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-0.5 h-12 bg-gradient-to-b from-cyan-500 to-transparent" />
                  <div>
                    <div className="font-accent text-3xl font-bold text-cyan-400">7+</div>
                    <div className="text-gray-500 text-sm uppercase tracking-wider">Years Experience</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-0.5 h-12 bg-gradient-to-b from-cyan-500 to-transparent" />
                  <div>
                    <div className="font-accent text-3xl font-bold text-cyan-400">100%</div>
                    <div className="text-gray-500 text-sm uppercase tracking-wider">Guaranteed</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                {/* Service Selection Dropdown */}
                <div className="relative" ref={menuRef}>
                  <button 
                    onClick={() => {
                      const newState = !isServiceMenuOpen;
                      setIsServiceMenuOpen(newState);
                      if (newState && menuRef.current) {
                        setTimeout(() => {
                          menuRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 100);
                      }
                    }}
                    className="btn-primary flex items-center gap-2"
                    data-testid="hero-book-btn"
                  >
                    Get Started <ChevronDown className={`w-5 h-5 transition-transform ${isServiceMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <AnimatePresence>
                    {isServiceMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute bottom-full left-0 mb-2 w-64 bg-black border border-cyan-500/30 rounded-sm shadow-2xl z-50 overflow-hidden sm:bottom-auto sm:top-full sm:mt-2 sm:mb-0"
                      >
                        <div className="p-3 border-b border-white/10">
                          <p className="text-sm text-gray-400">What service are you interested in?</p>
                        </div>
                        <div className="py-2">
                          {serviceOptions.map((option) => (
                            <Link
                              key={option.path}
                              to={option.path}
                              onClick={() => setIsServiceMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-3 text-white hover:bg-cyan-500/10 hover:text-cyan-400 transition-colors"
                            >
                              <span className="text-cyan-500">{option.icon}</span>
                              <span className="font-medium">{option.name}</span>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                
                <ContactDropdown 
                  label="Contact Us" 
                  className="btn-secondary"
                  testId="hero-contact-dropdown"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_ceramic-experts-1/artifacts/roiudqcn_aebe0e016e57417f4f9a44e0119f71c1.jpg"
                alt="Noble Detailing team providing ceramic coating service in Fresno"
                className="rounded-sm border border-white/10"
              />
              <div className="absolute -bottom-6 -left-6 bg-black/90 border border-cyan-500/30 p-4">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                </div>
                <p className="text-sm text-gray-400 mt-1">5-Star Rated in Fresno</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-padding bg-[#0a0a0a]" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="font-accent text-cyan-400 text-lg font-semibold">02</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mt-4 mb-4 uppercase tracking-wider">
              Our Services
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto" />
            <p className="text-gray-400 mt-6 max-w-2xl mx-auto">
              Premium auto detailing services in Fresno, Madera, Clovis & surrounding areas. From ceramic coating to paint correction, we deliver showroom-quality results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link 
                  to={service.link}
                  className="service-card block h-full bg-neutral-900/50 border border-white/5 p-8 group"
                  data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-16 h-16 rounded-sm bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mb-6 group-hover:bg-cyan-500/20 transition-colors">
                    {service.icon}
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-4 uppercase tracking-wider group-hover:text-cyan-400 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <div className="flex items-center gap-2 text-cyan-400 text-sm font-semibold uppercase tracking-wider">
                    Learn More <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={() => scrollToSection('packages')}
              className="btn-primary"
              data-testid="view-packages-btn"
            >
              View Packages
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Us - SEO Rich Section */}
      <section id="why-choose-us" className="section-padding" data-testid="why-choose-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-accent text-cyan-400 text-lg font-semibold">03</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mt-4 mb-6 uppercase tracking-wider">
                Fresno's Trusted Auto Detailing Experts
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-transparent mb-8" />
              
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p>
                  <strong className="text-white">Noble Detailing</strong> has been Central California's premier choice for professional <strong>mobile auto detailing</strong>, <strong>ceramic coating</strong>, and <strong>paint correction</strong> services. We proudly serve <strong>Fresno, Madera, Clovis, Chowchilla</strong>, and all surrounding areas.
                </p>
                <p>
                  Unlike traditional car washes, our <strong>professional detailing services</strong> use industry-leading products and techniques to restore, protect, and maintain your vehicle's appearance. Whether you drive a daily commuter or a show-quality exotic, we treat every vehicle with the same meticulous attention to detail.
                </p>
                <p>
                  Our <strong>mobile detailing service</strong> brings the detail shop to you. We arrive fully equipped with professional-grade products, purified water systems, and all the tools needed to deliver showroom results—right in your driveway or office parking lot.
                </p>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-cyan-500" />
                  <span className="text-sm">100% Mobile Service</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-cyan-500" />
                  <span className="text-sm">Certified Installers</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-cyan-500" />
                  <span className="text-sm">Premium Products</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-cyan-500" />
                  <span className="text-sm">Satisfaction Guaranteed</span>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-neutral-900/50 border border-white/5 p-6 hover:border-cyan-500/30 transition-all">
                <h3 className="font-heading text-lg font-semibold uppercase tracking-wider text-cyan-400 mb-3">
                  Ceramic Coating Fresno
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Professional ceramic coating installation for long-lasting paint protection. Our coatings provide 2-5+ years of UV protection, hydrophobic properties, and enhanced gloss for vehicles in the Fresno area.
                </p>
                <Link to="/ceramic-coating-fresno" className="inline-flex items-center gap-2 text-cyan-400 text-sm mt-3 hover:text-cyan-300">
                  Learn More <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="bg-neutral-900/50 border border-white/5 p-6 hover:border-cyan-500/30 transition-all">
                <h3 className="font-heading text-lg font-semibold uppercase tracking-wider text-cyan-400 mb-3">
                  Paint Correction Madera & Clovis
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Expert swirl mark and scratch removal using professional polishing techniques. Restore your paint's mirror-like finish before applying ceramic coating for optimal results.
                </p>
                <Link to="/paint-correction-fresno" className="inline-flex items-center gap-2 text-cyan-400 text-sm mt-3 hover:text-cyan-300">
                  Learn More <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="bg-neutral-900/50 border border-white/5 p-6 hover:border-cyan-500/30 transition-all">
                <h3 className="font-heading text-lg font-semibold uppercase tracking-wider text-cyan-400 mb-3">
                  Mobile Detailing Chowchilla
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Complete interior and exterior detailing delivered to your location. We service all of Central California including Fresno, Madera, Clovis, Chowchilla, Sanger, and Selma.
                </p>
                <Link to="/mobile-detailing-fresno" className="inline-flex items-center gap-2 text-cyan-400 text-sm mt-3 hover:text-cyan-300">
                  Learn More <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="section-padding bg-[#0a0a0a]" data-testid="gallery-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="font-accent text-cyan-400 text-lg font-semibold">04</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mt-4 mb-4 uppercase tracking-wider">
              Before & After
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto" />
            <p className="text-gray-400 mt-6 max-w-2xl mx-auto">
              See the transformation our professional detailing brings to vehicles in Fresno and surrounding areas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {galleryItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <p className="text-cyan-400 text-sm uppercase tracking-wider mb-4 font-semibold">
                  {item.label}
                </p>
                <BeforeAfterSlider
                  beforeImage={item.before}
                  afterImage={item.after}
                  className="aspect-[4/5]"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="section-padding" data-testid="packages-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="font-accent text-cyan-400 text-lg font-semibold">05</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mt-4 mb-4 uppercase tracking-wider">
              Packages
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent mx-auto" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`pricing-card flex flex-col p-8 bg-neutral-900 border ${
                  pkg.featured ? 'border-cyan-500 featured scale-105' : 'border-white/10'
                } relative`}
                data-testid={`package-card-${index}`}
              >
                {pkg.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-cyan-500 text-black text-xs font-bold uppercase tracking-wider px-4 py-2">
                    Most Popular
                  </div>
                )}
                
                <div className="text-center pb-8 border-b border-white/10 mb-8">
                  <h3 className="font-heading text-xl font-bold uppercase tracking-wider mb-2">
                    {pkg.name}
                  </h3>
                  <p className="text-cyan-400 text-sm tracking-wider mb-6">
                    {pkg.subtitle}
                  </p>
                  <div className="font-accent">
                    <span className="text-5xl font-bold text-cyan-400">{pkg.price}</span>
                    <span className="text-gray-500">{pkg.priceLabel}</span>
                  </div>
                </div>

                <p className="text-gray-400 leading-relaxed flex-grow mb-8">
                  {pkg.description}
                </p>

                <button 
                  onClick={() => openBooking(pkg.packageLevel)}
                  className={`w-full py-4 font-semibold uppercase tracking-wider transition-all ${
                    pkg.featured 
                      ? 'bg-cyan-500 text-black hover:bg-cyan-400' 
                      : 'border border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10'
                  }`}
                  data-testid={`select-package-${index}`}
                >
                  Select Package
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <div className="bg-[#0a0a0a]">
        <FAQ items={faqItems} title="Frequently Asked Questions" />
      </div>

      {/* Contact Section */}
      <section id="contact" className="section-padding" data-testid="contact-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-accent text-cyan-400 text-lg font-semibold">06</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold mt-4 mb-6 uppercase tracking-wider">
                Book Your Service
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-transparent mb-8" />
              
              <p className="text-gray-400 text-lg leading-relaxed mb-10">
                Experience premium mobile detailing delivered to your location in <strong className="text-white">Fresno, Madera, Clovis, Chowchilla</strong> and surrounding Central California areas.
              </p>

              <div className="space-y-6">
                <ContactDropdown 
                  label="Contact Us" 
                  className="flex items-center gap-2 text-white font-accent text-xl font-semibold border border-cyan-500/30 px-5 py-3 rounded-sm hover:bg-cyan-500/10 transition-colors"
                  testId="contact-section-dropdown"
                />
                
                <div className="flex items-center gap-4 text-gray-400">
                  <div className="w-12 h-12 rounded-sm bg-white/5 border border-white/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-cyan-400" />
                  </div>
                  <span>Fresno, Madera, Clovis & Surrounding Areas</span>
                </div>
                
                <div className="flex items-center gap-4 text-gray-400">
                  <div className="w-12 h-12 rounded-sm bg-white/5 border border-white/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-cyan-400" />
                  </div>
                  <span>Available 7 Days a Week</span>
                </div>
              </div>
            </div>

            <div>
              <div className="p-8 bg-gradient-to-r from-cyan-500 to-cyan-400 text-black text-center">
                <span className="font-heading text-2xl font-bold uppercase tracking-wider flex items-center justify-center gap-4 mb-4">
                  Get Started
                </span>
                <p className="text-black/70 mb-6">What service are you interested in?</p>
                <div className="flex flex-col gap-3">
                  {serviceOptions.map((option) => (
                    <Link
                      key={option.path}
                      to={option.path}
                      className="flex items-center justify-center gap-3 px-4 py-3 bg-black/20 hover:bg-black/30 text-black font-semibold rounded-sm transition-colors"
                      data-testid={`bottom-service-${option.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {option.icon}
                      <span>{option.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-neutral-900 border border-white/10">
                <h4 className="font-heading text-lg font-semibold uppercase tracking-wider mb-4">
                  Service Areas
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {['Fresno', 'Madera', 'Clovis', 'Chowchilla', 'Sanger', 'Selma'].map((city) => (
                    <div key={city} className="flex items-center gap-2 text-gray-400">
                      <CheckCircle className="w-4 h-4 text-cyan-500" />
                      <span>{city}, CA</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
